
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type SkinnedDebugShaderUniforms = {
	uProjectionMatrix: WebGLUniformLocation,
	uModelViewMatrix: WebGLUniformLocation,
	uBindShapeMatrix: WebGLUniformLocation,
	uMatrixSampler: WebGLUniformLocation,
	uDataSettings: WebGLUniformLocation,
	uBlendSettings: WebGLUniformLocation,
	uBoneOverrides: WebGLUniformLocation[],
	uBoneOverrideIndices: WebGLUniformLocation[],
	uBoneOverrideCount: WebGLUniformLocation
};

export type SkinnedDebugShaderAttributes = {
	aVertexPosition: number,
	aVertexColor: number,
	aJointWeights: number,
	aJointIndices: number,
	vVertexColor: number
};

export type SkinnedDebugShaderSettings = ('BONE_OVERRIDES')[];

export class SkinnedDebugShader extends ShaderBase<SkinnedDebugShaderUniforms, SkinnedDebugShaderAttributes> {

    private static fragmentShaderSource = `
in vec4 vVertexColor;
out vec4 fragColor;

void main(void) {
	fragColor = vVertexColor;
}
`;

    private static vertexShaderSource = `
// Skinned debug visualizer: GPU-skins static debug geometry (octahedrons,
// lines, capsules) using the same animation data texture as the main mesh
// shaders. Vertices carry a single joint index in aJointIndices.x with
// weight 1.0 in aJointWeights.x — the other 3 slots are unused but the
// _shared/animation.glsl read_animation() loop handles them as no-ops.

uniform mat4 uProjectionMatrix;
uniform mat4 uModelViewMatrix;

layout(location = 0) in vec3 aVertexPosition;
layout(location = 3) in vec4 aVertexColor;

out vec4 vVertexColor;

#define NOODLE_BONE_OVERRIDES
// --- begin: animation.glsl ---
layout(location = 4) in vec4 aJointWeights;
layout(location = 5) in vec4 aJointIndices;

uniform mat4 uBindShapeMatrix;
uniform sampler2D uMatrixSampler;
uniform vec3 uDataSettings;
uniform vec4 uBlendSettings;

#ifdef NOODLE_BONE_OVERRIDES
uniform mat4 uBoneOverrides[32];
uniform int uBoneOverrideIndices[32];
uniform int uBoneOverrideCount;
#endif

mat4 read_matrix(sampler2D tex, float i, vec2 size) {
	const float offset = 0.5;
	float tx = mod(i * 4.0, size.x) + offset;
	float ty = floor(i * 4.0 / size.x) + offset;

	return mat4(
		texture(tex, vec2((tx + 0.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 1.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 2.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 3.0) / size.x, ty / size.y))
	);
}

mat4 read_animation() {
	mat4 matrix = mat4(0.0);
	for (int j = 0; j < 4; j++) {
		float w = aJointWeights[j];
		if (w > 0.0) {
			float jointIndex = float(aJointIndices[j]);
			float index = jointIndex + uDataSettings.z;
			mat4 jointMatrix = read_matrix(uMatrixSampler, index, uDataSettings.xy);

			// Blend with previous clip if active
			if (uBlendSettings.w > 0.0) {
				mat4 prevMatrix = read_matrix(uMatrixSampler, jointIndex + uBlendSettings.z, uBlendSettings.xy);
				jointMatrix = prevMatrix + (jointMatrix - prevMatrix) * uBlendSettings.w;
			}

			#ifdef NOODLE_BONE_OVERRIDES
			for (int k = 0; k < uBoneOverrideCount; k++) {
				if (uBoneOverrideIndices[k] == int(jointIndex)) {
					jointMatrix = uBoneOverrides[k];
					break;
				}
			}
			#endif

			matrix += jointMatrix * w;
		}
	}
	return matrix;
}

// --- end: animation.glsl ---

void main(void) {
	mat4 animationMatrix = read_animation();
	vec4 pos = vec4(aVertexPosition, 1.0) * uBindShapeMatrix * animationMatrix;
	gl_Position = uProjectionMatrix * uModelViewMatrix * vec4(pos.xyz, 1.0);
	vVertexColor = aVertexColor;
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: SkinnedDebugShaderUniformsContainer;
    public attributesContainer: SkinnedDebugShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: SkinnedDebugShaderSettings) {
        super(ctx, SkinnedDebugShader.fragmentShaderSource, SkinnedDebugShader.vertexShaderSource, settings);
        this.uniformsContainer = new SkinnedDebugShaderUniformsContainer(ctx, this);
        this.attributesContainer = new SkinnedDebugShaderShaderAttributesContainer(ctx, this);
    }
}

class SkinnedDebugShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: SkinnedDebugShader) {
        super(ctx);
    }

    public setProjectionMatrix(value: mat4 | number[]) : SkinnedDebugShaderUniformsContainer {
        this.setValue('uProjectionMatrix', this.shader.uniforms.uProjectionMatrix, value, 'mat4');
        return this;
    }

    public setModelViewMatrix(value: mat4 | number[]) : SkinnedDebugShaderUniformsContainer {
        this.setValue('uModelViewMatrix', this.shader.uniforms.uModelViewMatrix, value, 'mat4');
        return this;
    }

    public setBindShapeMatrix(value: mat4 | number[]) : SkinnedDebugShaderUniformsContainer {
        this.setValue('uBindShapeMatrix', this.shader.uniforms.uBindShapeMatrix, value, 'mat4');
        return this;
    }

    public setMatrixSampler(texture: Texture) : SkinnedDebugShaderUniformsContainer {
        this.setTextureValue('uMatrixSampler', this.shader.uniforms.uMatrixSampler, texture, 'sampler2D');
        return this;
    }

    public setDataSettings(value: vec3 | number[]) : SkinnedDebugShaderUniformsContainer {
        this.setValue('uDataSettings', this.shader.uniforms.uDataSettings, value, 'vec3');
        return this;
    }

    public setBlendSettings(value: vec4 | number[]) : SkinnedDebugShaderUniformsContainer {
        this.setValue('uBlendSettings', this.shader.uniforms.uBlendSettings, value, 'vec4');
        return this;
    }

    public setBoneOverrides(index: number, value: mat4 | number[]) : SkinnedDebugShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrides: ' + index);
        this.setValue('uBoneOverrides', this.shader.uniforms.uBoneOverrides[index], value, 'mat4');
        return this;
    }

    public setBoneOverrideIndices(index: number, value: number) : SkinnedDebugShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrideIndices: ' + index);
        this.setValue('uBoneOverrideIndices', this.shader.uniforms.uBoneOverrideIndices[index], value, 'int');
        return this;
    }

    public setBoneOverrideCount(value: number) : SkinnedDebugShaderUniformsContainer {
        this.setValue('uBoneOverrideCount', this.shader.uniforms.uBoneOverrideCount, value, 'int');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uMatrixSampler', uniform: this.shader.uniforms.uMatrixSampler, dataType: 'sampler2D' }
        ];
    }
}

class SkinnedDebugShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: SkinnedDebugShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : SkinnedDebugShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexColor(settings: { length: number, offset: number, itemSize: number }) : SkinnedDebugShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexColor, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointWeights(settings: { length: number, offset: number, itemSize: number }) : SkinnedDebugShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointWeights, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointIndices(settings: { length: number, offset: number, itemSize: number }) : SkinnedDebugShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointIndices, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvVertexColor(settings: { length: number, offset: number, itemSize: number }) : SkinnedDebugShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vVertexColor, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}